<template>
  <b-modal 
    :title="modalTitle" 
    :modelValue="showModal" 
    body-class="pt-0" 
    @update:modelValue="$emit('update:showModal', $event)"
  >
    <template #default>
      <form>
        <div class="row mt-2">
          <div class="col-sm-12 d-flex justify-content-between align-items-center pb-0">
            <label for="bc_amount" class="col-form-label text-capitalize-first">{{ helper.trans('labels.amount') }}</label>
          </div>
          <div class="col-sm-12">
            <FormInputWrapper v-slot="parentProps" :errors="balanceErrors.amount">
              <input 
                :disabled="isSubmitting" 
                v-model="balanceForm.amount" 
                :class="parentProps.class" 
                type="text" 
                class="form-control" 
                id="bc_amount"
                @input="handleAmountInput"
              >
            </FormInputWrapper>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-sm-12 d-flex justify-content-between align-items-center pb-0">
            <label for="bc_comment" class="col-form-label text-capitalize-first">{{ helper.trans('labels.comment') }}</label>
          </div>
          <div class="col-sm-12">
            <FormInputWrapper v-slot="parentProps" :errors="balanceErrors.comment">
              <input 
                :disabled="isSubmitting" 
                v-model="balanceForm.comment" 
                :class="parentProps.class" 
                type="text" 
                class="form-control" 
                id="bc_comment"
                @input="handleCommentInput"
              >
            </FormInputWrapper>
          </div>
        </div>
      </form>
    </template>
    <template #footer>
      <button 
        type="button" 
        :disabled="isSubmitting" 
        @click="$emit('update:showModal', false)" 
        class="btn btn-outline-secondary btn-sm text-capitalize-first"
      >
        {{ helper.trans('button.cancel') }}
      </button>
      <LoaderWrapper ref="loaderBalanceCorrection" :zoom="0.5">
        <button 
          type="button" 
          :disabled="!isBalanceFormValid || isSubmitting" 
          @click="submitBalanceCorrection" 
          class="btn btn-outline-primary btn-sm text-capitalize-first"
        >
          {{ helper.trans('button.confirm') }}
        </button>
      </LoaderWrapper>
    </template>
  </b-modal>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, computed, watch } from 'vue'
import { helper } from "@/service/CommonHelper"
import { useNotification } from "@kyvg/vue3-notification"
import { internalTransferRequests } from "@/service/api/internalTransferRequests"
import LoaderWrapper from "@/components/LoaderWrapper.vue"
import FormInputWrapper from "@/components/FormInputWrapper.vue"
import ValidationErrorDTO from "@/models/ValidationErrorDTO"

const notification = useNotification()

export default defineComponent({
  name: 'BalanceCorrectionModal',
  components: {
    LoaderWrapper,
    FormInputWrapper
  },
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    accountId: {
      type: String,
      default: null
    },
    providerName: {
      type: String,
      default: ''
    },
    entityName: {
      type: String,
      default: ''
    },
    entityType: {
      type: String,
      default: ''
    }
  },
  emits: ['update:showModal', 'success'],
  setup(props, { emit }) {
    const loaderBalanceCorrection = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    
    const balanceForm = reactive({ amount: '', comment: '' })
    const balanceErrors = reactive({ amount: [] as ValidationErrorDTO[], comment: [] as ValidationErrorDTO[] })
    const balanceFormTouched = ref(false)
    const isSubmitting = ref(false)

    const modalTitle = computed(() => {
      const name = props.entityName || props.providerName
      if (props.entityType === 'store') {
        return helper.trans('labels.balance_correction_store', { name })
      }
      if (props.accountId) {
        return helper.trans('labels.balance_correction_provider', { name })
      }
      return helper.trans('labels.balance_correction')
    })

    const validateAmount = (value: string): ValidationErrorDTO[] => {
      const errors: ValidationErrorDTO[] = []
      if (!value || value.trim() === '') {
        errors.push({ code: 'required', field: 'amount', message: helper.trans('validation.required') })
        return errors
      }
      const normalized = value.replace(/\s/g, '').replace(',', '.')
      if (!/^[-+]?\d*[.,]?\d*$/.test(normalized) || normalized === '' || normalized === '.' || normalized === ',') {
        errors.push({ code: 'amount', field: 'amount', message: helper.trans('validation.amount') })
      }
      return errors
    }

    const validateComment = (value: string): ValidationErrorDTO[] => {
      const errors: ValidationErrorDTO[] = []
      if (value && value.trim() !== '' && value.length > 200) {
        errors.push({ code: 'length', field: 'comment', message: helper.trans('validation.length') })
      }
      return errors
    }

    const isBalanceFormValid = computed<boolean>(() => {
      const amountErrors = validateAmount(balanceForm.amount)
      const commentErrors = validateComment(balanceForm.comment)
      
      if (balanceFormTouched.value) {
        balanceErrors.amount = amountErrors
        balanceErrors.comment = commentErrors
      } else {
        balanceErrors.amount = []
        balanceErrors.comment = []
      }

      return amountErrors.length === 0 && commentErrors.length === 0
    })

    const submitBalanceCorrection = async () => {
      balanceFormTouched.value = true
      
      if (!isBalanceFormValid.value) {
        return
      }
      
      try {
        isSubmitting.value = true
        loaderBalanceCorrection.value?.show()
        
        const requestData: any = {
          amount: balanceForm.amount.replace(/\s/g, '').replace(',', '.'),
          comment: balanceForm.comment.trim(),
        }

        if (props.accountId) {
          requestData.accountId = props.accountId
        }
        
        await internalTransferRequests.balanceCorrection(requestData)
        
        const name = props.entityName || props.providerName
        const successMessage = props.entityType === 'store'
          ? helper.trans('labels.balance_corrected_successfully_store', { name })
          : props.accountId
            ? helper.trans('labels.balance_corrected_successfully_provider', { name })
            : helper.trans('labels.balance_corrected_successfully')

        notification.notify({ 
          title: helper.trans('labels.success'), 
          text: successMessage, 
          type: 'success' 
        })
        
        emit('update:showModal', false)
        emit('success')

        balanceForm.amount = ''
        balanceForm.comment = ''
        balanceErrors.amount = []
        balanceErrors.comment = []
        balanceFormTouched.value = false
        
      } catch (e: any) {
        const errorMessage = props.entityType === 'store'
          ? helper.trans('labels.balance_correction_failed_store')
          : props.accountId
            ? helper.trans('labels.balance_correction_failed_provider')
            : helper.trans('labels.balance_correction_failed')

        notification.notify({ 
          title: helper.trans('labels.error'), 
          text: errorMessage, 
          type: 'error' 
        })
      } finally {
        isSubmitting.value = false
        loaderBalanceCorrection.value?.hide()
      }
    }

    const handleAmountInput = () => {
      balanceFormTouched.value = true
    }

    const handleCommentInput = () => {
      balanceFormTouched.value = true
    }

    watch(() => props.showModal, (newValue) => {
      if (newValue) {
        balanceForm.amount = ''
        balanceForm.comment = ''
        balanceErrors.amount = []
        balanceErrors.comment = []
        balanceFormTouched.value = false
      }
    })

    return {
      helper,
      loaderBalanceCorrection,
      balanceForm,
      balanceErrors,
      isSubmitting,
      isBalanceFormValid,
      modalTitle,
      submitBalanceCorrection,
      balanceFormTouched,
      handleAmountInput,
      handleCommentInput
    }
  }
})
</script>
